import {
  addMonthsIso,
  type EmployeeListItem,
  type FileItem,
  type Paginated,
  SAFETY_CATEGORIES,
  SAFETY_CATEGORY_LABELS,
  SAFETY_RESULTS,
  SAFETY_STATE_LABELS,
  type SafetyKindItem,
  type SafetyRecordItem,
  type SafetyRequirementItem,
  type SafetyResult,
  type SafetyState,
  safetyResultLabel,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import { Check, Clock, Search, Users, X } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useId, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { formatDay, formatMoment, plural, todayIso } from '@/lib/format';
import { departmentsTreeQuery, flattenTree } from '@/lib/queries';
import { safetyCountersQuery, safetyMetaQuery, signRecord } from '@/lib/safety';
import { Dialog } from './dialog';
import { FileList, FileUpload } from './file-upload';
import { Badge, Button, Dl, Field, Input, Select, Textarea } from './ui';
import { UserPicker } from './user-picker';

// ---------- Навигация раздела ----------

export function SafetyNav() {
  const can = useCan();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const counters = useQuery(safetyCountersQuery);
  const staff = can('safety.read_all') || can('safety.manage');
  const mine = (counters.data?.unsigned ?? 0) + (counters.data?.conduct ?? 0);
  const tabs: { to: string; label: string; show: boolean; badge?: number }[] = [
    { to: '/safety', label: 'Сводка', show: staff },
    { to: '/safety/journal', label: 'Журнал', show: staff },
    { to: '/safety/my', label: 'Мои', show: true, badge: mine || undefined },
    { to: '/safety/settings', label: 'Настройки', show: can('safety.settings.manage') },
  ];
  const active = (to: string) =>
    to === '/safety'
      ? pathname === '/safety' || pathname.startsWith('/safety/employees')
      : pathname === to || pathname.startsWith(`${to}/`);
  return (
    <nav
      aria-label="Разделы охраны труда"
      className="-mx-4 mb-5 overflow-x-auto border-b border-line px-4 lg:-mx-8 lg:px-8"
    >
      <ul className="flex gap-1">
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <li key={t.to}>
              <Link
                to={t.to}
                aria-current={active(t.to) ? 'page' : undefined}
                className={clsx(
                  'relative inline-flex h-10 items-center gap-1.5 whitespace-nowrap px-3 text-[14px]',
                  active(t.to) ? 'font-semibold text-ink' : 'text-steel hover:text-ink',
                )}
              >
                {t.label}
                {t.badge ? (
                  <span className="min-w-[18px] rounded-full bg-danger px-1.5 text-center text-[11px] font-semibold leading-[17px] tabular-nums text-white">
                    {t.badge}
                  </span>
                ) : null}
                {active(t.to) && (
                  <span aria-hidden className="absolute inset-x-2 -bottom-px h-[3px] rounded-t bg-signal" />
                )}
              </Link>
            </li>
          ))}
      </ul>
    </nav>
  );
}

// ---------- Метки ----------

const STATE_TONES: Record<SafetyState, 'neutral' | 'ok' | 'warn' | 'danger'> = {
  missing: 'danger',
  overdue: 'danger',
  soon: 'warn',
  ok: 'ok',
  exempt: 'neutral',
};

export function StateBadge({ state }: { state: SafetyState }) {
  return <Badge tone={STATE_TONES[state]}>{SAFETY_STATE_LABELS[state]}</Badge>;
}

/** «до 12 мая 2027 · через 14 дн.» / «просрочено на 3 дн.» */
export function DueText({ item }: { item: Pick<SafetyRequirementItem, 'nextDueAt' | 'daysLeft' | 'state'> }) {
  if (item.state === 'exempt') return <span className="text-steel">—</span>;
  if (!item.nextDueAt)
    return <span className="text-steel">{item.state === 'missing' ? 'нужно пройти' : 'разово'}</span>;
  const d = item.daysLeft ?? 0;
  return (
    <span>
      {formatDay(item.nextDueAt)}
      <span
        className={clsx(
          'block text-[12.5px]',
          d < 0 ? 'text-danger' : d <= 30 ? 'text-[#7a5b00]' : 'text-steel',
        )}
      >
        {d < 0
          ? `просрочено на ${-d} ${plural(-d, ['день', 'дня', 'дней'])}`
          : d === 0
            ? 'сегодня последний день'
            : `через ${d} ${plural(d, ['день', 'дня', 'дней'])}`}
      </span>
    </span>
  );
}

/** Подпись в журнале: подтверждено в системе / ждёт / не нужна. */
export function SignMark({ at, needed = true }: { at: string | null; needed?: boolean }) {
  if (!needed) return <span className="text-steel">не нужна</span>;
  if (at)
    return (
      <span className="inline-flex items-center gap-1 text-ok" title={`Подтверждено ${formatMoment(at)}`}>
        <Check className="size-3.5" aria-hidden />
        {formatMoment(at)}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[#7a5b00]">
      <Clock className="size-3.5" aria-hidden />
      ждёт
    </span>
  );
}

export function ResultText({ r }: { r: Pick<SafetyRecordItem, 'result' | 'kind'> }) {
  return (
    <span
      className={clsx(r.result === 'failed' && 'text-danger', r.result === 'limited' && 'text-[#7a5b00]')}
    >
      {safetyResultLabel(r.kind.category, r.result)}
    </span>
  );
}

export const conductorText = (r: SafetyRecordItem) =>
  [r.conductedBy?.fullName, r.conductedByName].filter(Boolean).join(', ') || '—';

// ---------- Выбор сотрудников (по карточкам, а не учёткам: у рабочих учёток может не быть) ----------

interface EmployeeOption {
  id: string;
  fullName: string;
  hint: string | null;
}

const toOption = (e: EmployeeListItem): EmployeeOption => ({
  id: e.id,
  fullName: e.fullName,
  hint: [e.positionName, e.departmentName].filter(Boolean).join(' · ') || null,
});

export function EmployeesPicker({
  value,
  onChange,
  id,
  invalid,
}: {
  value: EmployeeOption[];
  onChange: (v: EmployeeOption[]) => void;
  id?: string;
  invalid?: boolean;
}) {
  const listId = useId();
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [dep, setDep] = useState('');
  const tree = useQuery(departmentsTreeQuery);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(text.trim()), 200);
    return () => window.clearTimeout(t);
  }, [text]);
  const found = useQuery({
    queryKey: ['org', 'employees', 'pick', debounced],
    queryFn: () =>
      api<Paginated<EmployeeListItem>>('/org/employees', { query: { q: debounced, pageSize: 20 } }),
    enabled: open && debounced.length > 0,
  });
  const options = (found.data?.items ?? []).filter((e) => e.isActive && !value.some((v) => v.id === e.id));
  const addDepartment = useMutation({
    mutationFn: (departmentId: string) =>
      api<Paginated<EmployeeListItem>>('/org/employees', {
        query: { departmentId, includeSubdepartments: 'true', pageSize: 200 },
      }),
    onSuccess: (res) => {
      const extra = res.items.filter((e) => e.isActive && !value.some((v) => v.id === e.id)).map(toOption);
      onChange([...value, ...extra]);
      setDep('');
    },
  });

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((v) => (
            <li
              key={v.id}
              className="inline-flex max-w-full items-center gap-1 rounded bg-sunken py-0.5 pl-2 pr-1 text-[13px]"
            >
              <span className="truncate">{v.fullName}</span>
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x.id !== v.id))}
                aria-label={`Убрать ${v.fullName}`}
                className="inline-flex size-5 items-center justify-center rounded text-steel hover:bg-line hover:text-ink"
              >
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
            aria-hidden
          />
          <Input
            id={id}
            aria-invalid={invalid}
            aria-controls={listId}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => window.setTimeout(() => setOpen(false), 150)}
            placeholder="Фамилия сотрудника"
            className="pl-9"
          />
          {open && options.length > 0 && (
            <ul
              id={listId}
              className="panel absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto py-1 shadow-lg"
            >
              {options.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left hover:bg-sunken"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange([...value, toOption(o)]);
                      setText('');
                    }}
                  >
                    {o.fullName}
                    <span className="block text-[12.5px] text-steel">{toOption(o).hint}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Select
          aria-label="Добавить весь отдел"
          value={dep}
          onChange={(e) => {
            setDep(e.target.value);
            if (e.target.value) addDepartment.mutate(e.target.value);
          }}
          className="sm:w-[220px]"
          disabled={addDepartment.isPending}
        >
          <option value="">+ весь отдел…</option>
          {flattenTree(tree.data ?? []).map(({ node, depth }) => (
            <option key={node.id} value={node.id}>
              {`${'  '.repeat(depth)}${node.name}`}
            </option>
          ))}
        </Select>
      </div>
      {value.length > 1 && (
        <p className="flex items-center gap-1.5 text-[12.5px] text-steel">
          <Users className="size-3.5" aria-hidden />
          {value.length} {plural(value.length, ['сотрудник', 'сотрудника', 'сотрудников'])} — запись появится
          у каждого
          <button type="button" className="link ml-1" onClick={() => onChange([])}>
            очистить
          </button>
        </p>
      )}
    </div>
  );
}

// ---------- Форма записи ----------

function useSafetySave<T>(fn: (body: Record<string, unknown>) => Promise<T>, onDone: () => void) {
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['safety'] });
      onDone();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(Object.keys(err.fields).length && !err.fields._ ? null : (err.fields._ ?? err.message));
      } else setFormError(err.message);
    },
  });
  return { m, errors, setErrors, formError };
}

export function FormError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p
      role="alert"
      className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13.5px] text-danger"
    >
      {children}
    </p>
  );
}

function groupedKinds(kinds: SafetyKindItem[]) {
  return SAFETY_CATEGORIES.map((c) => ({ category: c, kinds: kinds.filter((k) => k.category === c) })).filter(
    (g) => g.kinds.length,
  );
}

export interface RecordDefaults {
  kindId?: string;
  employees?: EmployeeOption[];
}

/**
 * Новая запись (сразу на группу) или правка одной.
 * Для инструктажа «кто проводил» — сотрудник; для медосмотра и обучения обычно сторонняя организация.
 */
export function RecordFormDialog({
  record,
  defaults,
  onClose,
}: {
  record?: SafetyRecordItem;
  defaults?: RecordDefaults;
  onClose: () => void;
}) {
  const meta = useQuery(safetyMetaQuery);
  const kinds = (meta.data?.kinds ?? []).filter((k) => k.isActive || k.id === record?.kind.id);
  const [kindId, setKindId] = useState(record?.kind.id ?? defaults?.kindId ?? kinds[0]?.id ?? '');
  const kind = kinds.find((k) => k.id === kindId);
  const [employees, setEmployees] = useState<EmployeeOption[]>(
    record
      ? [{ id: record.employee.id, fullName: record.employee.fullName, hint: null }]
      : (defaults?.employees ?? []),
  );
  const [date, setDate] = useState(record?.date ?? todayIso());
  const [result, setResult] = useState<SafetyResult>(record?.result ?? 'passed');
  const [conductor, setConductor] = useState<string[]>(
    record?.conductedBy ? [record.conductedBy.userId] : [],
  );
  const [file, setFile] = useState<FileItem[]>(record?.file ? [record.file] : []);
  const { m, errors, setErrors, formError } = useSafetySave(
    (body) =>
      record
        ? api(`/safety/records/${record.id}`, { method: 'PATCH', body })
        : api('/safety/records', { method: 'POST', body }),
    onClose,
  );

  useEffect(() => {
    if (!kindId && kinds[0]) setKindId(kinds[0].id);
  }, [kindId, kinds]);

  const autoDue =
    kind?.periodMonths && result !== 'failed' && date ? addMonthsIso(date, kind.periodMonths) : null;
  const instruction = kind?.category === 'instruction';
  const signed = Boolean(record?.employeeSignedAt || record?.conductorSignedAt);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const s = (k: string) => String(fd.get(k) ?? '').trim();
    if (!record && !employees.length) return setErrors({ employeeIds: 'Выберите сотрудников' });
    if (!kindId) return setErrors({ kindId: 'Выберите вид' });
    if (kind?.reasonRequired && !s('reason')) return setErrors({ reason: 'Укажите причину проведения' });
    const body: Record<string, unknown> = {
      date,
      conductedById: conductor[0] ?? null,
      conductedByName: s('conductedByName') || null,
      reason: kind?.reasonRequired || s('reason') ? s('reason') || null : null,
      result,
      documentNo: s('documentNo') || null,
      notes: s('notes') || null,
      fileId: file[0]?.id ?? null,
    };
    const due = s('nextDueAt');
    if (due) body.nextDueAt = due;
    else if (record) body.nextDueAt = null;
    if (!record) {
      body.kindId = kindId;
      body.employeeIds = employees.map((x) => x.id);
    }
    m.mutate(body);
  }

  return (
    <Dialog title={record ? `Запись №${record.number}` : 'Запись в журнал'} onClose={onClose} width={680}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-5">
        <Field label="Что прошли" error={errors.kindId}>
          {(p) => (
            <Select
              {...p}
              value={kindId}
              onChange={(e) => setKindId(e.target.value)}
              disabled={Boolean(record)}
            >
              {groupedKinds(kinds).map((g) => (
                <optgroup key={g.category} label={SAFETY_CATEGORY_LABELS[g.category]}>
                  {g.kinds.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
          )}
        </Field>
        {record ? (
          <Dl items={[['Сотрудник', record.employee.fullName]]} />
        ) : (
          <Field label="Кто прошёл" error={errors.employeeIds}>
            {(p) => (
              <EmployeesPicker
                id={p.id}
                invalid={p['aria-invalid']}
                value={employees}
                onChange={setEmployees}
              />
            )}
          </Field>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Дата" error={errors.date}>
            {(p) => (
              <Input
                {...p}
                type="date"
                value={date}
                max={todayIso()}
                onChange={(e) => setDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="Результат" error={errors.result}>
            {(p) => (
              <Select {...p} value={result} onChange={(e) => setResult(e.target.value as SafetyResult)}>
                {SAFETY_RESULTS.map((r) => (
                  <option key={r} value={r}>
                    {safetyResultLabel(kind?.category ?? 'instruction', r)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        {kind?.reasonRequired && (
          <Field label="Причина проведения" error={errors.reason}>
            {(p) => (
              <Input
                {...p}
                name="reason"
                defaultValue={record?.reason ?? ''}
                placeholder={
                  kind.name.startsWith('Целевой')
                    ? 'Например: разовые работы на высоте'
                    : 'Например: новое оборудование, новые правила'
                }
              />
            )}
          </Field>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={instruction ? 'Проводил' : 'Проводил (сотрудник)'}
            error={errors.conductedById}
            hint={instruction ? 'Получит запрос подтвердить проведение' : undefined}
          >
            {(p) => <UserPicker id={p.id} value={conductor} onChange={setConductor} placeholder="Фамилия" />}
          </Field>
          <Field
            label={instruction ? 'Или сторонний' : 'Организация'}
            error={errors.conductedByName}
            hint={instruction ? undefined : 'Клиника, учебный центр'}
          >
            {(p) => (
              <Input
                {...p}
                name="conductedByName"
                defaultValue={record?.conductedByName ?? ''}
                placeholder={kind?.category === 'medical' ? 'Медицинская организация' : 'Учебный центр'}
              />
            )}
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={kind?.category === 'medical' ? '№ заключения' : '№ протокола / документа'}
            error={errors.documentNo}
          >
            {(p) => <Input {...p} name="documentNo" defaultValue={record?.documentNo ?? ''} />}
          </Field>
          <Field
            label="Следующий срок"
            error={errors.nextDueAt}
            hint={
              autoDue
                ? `Пусто — посчитается сам: ${formatDay(autoDue)}`
                : kind?.periodMonths
                  ? 'Не засчитано — срока нет'
                  : 'Разовый — срока нет'
            }
          >
            {(p) => (
              <Input
                {...p}
                type="date"
                name="nextDueAt"
                defaultValue={record && record.nextDueAt !== autoDue ? (record.nextDueAt ?? '') : ''}
              />
            )}
          </Field>
        </div>
        <Field label="Файл (заключение, протокол, скан журнала)" error={errors.fileId}>
          {(p) => <FileUpload id={p.id} value={file} onChange={(f) => setFile(f.slice(-1))} />}
        </Field>
        <Field label="Примечание" error={errors.notes}>
          {(p) => <Textarea {...p} name="notes" rows={2} defaultValue={record?.notes ?? ''} />}
        </Field>
        {record && signed && (
          <p className="text-[12.5px] text-steel">
            Если изменить дату, проводившего или причину, подтверждения сбросятся — их попросят поставить
            заново.
          </p>
        )}
        <FormError>{formError}</FormError>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={m.isPending}>
            {record ? 'Сохранить' : employees.length > 1 ? `Внести (${employees.length})` : 'Внести'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Карточка записи ----------

export function RecordDialog({ record, onClose }: { record: SafetyRecordItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = () => qc.invalidateQueries({ queryKey: ['safety'] });
  const sign = useMutation({
    mutationFn: (as: 'employee' | 'conductor') => signRecord(record.id, as),
    onError: (err) => setError(err.message),
    onSuccess: () => {
      done();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/safety/records/${record.id}`, { method: 'DELETE' }),
    onError: (err) => setError(err.message),
    onSuccess: () => {
      done();
      onClose();
    },
  });
  if (editing) return <RecordFormDialog record={record} onClose={onClose} />;
  const r = record;
  return (
    <Dialog title={`${r.kind.name} · №${r.number}`} onClose={onClose} width={620}>
      <div className="flex flex-col gap-4 px-5 py-5">
        <Dl
          items={[
            ['Сотрудник', [r.employee.fullName, r.employee.positionName].filter(Boolean).join(', ')],
            ['Отдел', r.employee.departmentName],
            ['Дата', formatDay(r.date)],
            ...(r.reason ? ([['Причина', r.reason]] as [string, ReactNode][]) : []),
            ['Проводил', conductorText(r)],
            ['Результат', <ResultText key="r" r={r} />],
            ['Документ', r.documentNo],
            ['Следующий срок', r.nextDueAt ? formatDay(r.nextDueAt) : 'нет'],
            [
              'Сотрудник подтвердил',
              <SignMark
                key="e"
                at={r.employeeSignedAt}
                needed={r.kind.requiresSignature && Boolean(r.employee.userId)}
              />,
            ],
            [
              'Проводивший подтвердил',
              <SignMark key="c" at={r.conductorSignedAt} needed={Boolean(r.conductedBy)} />,
            ],
            ...(r.notes ? ([['Примечание', r.notes]] as [string, ReactNode][]) : []),
            [
              'Внёс',
              r.createdBy
                ? `${r.createdBy.fullName}, ${formatMoment(r.createdAt)}`
                : formatMoment(r.createdAt),
            ],
          ]}
        />
        {r.kind.requiresSignature && !r.employee.userId && (
          <p className="text-[12.5px] text-steel">
            У сотрудника нет учётки — подпись ставится в распечатанном журнале.
          </p>
        )}
        {r.file && <FileList files={[r.file]} />}
        {error && <FormError>{error}</FormError>}
        <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-4">
          {r.canEdit && (
            <>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() => {
                  if (window.confirm('Удалить запись из журнала?')) remove.mutate();
                }}
              >
                Удалить
              </Button>
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Изменить
              </Button>
            </>
          )}
          {r.canSignAsConductor && (
            <Button
              variant={r.canSignAsEmployee ? 'secondary' : 'primary'}
              loading={sign.isPending}
              onClick={() => sign.mutate('conductor')}
            >
              Подтвердить проведение
            </Button>
          )}
          {r.canSignAsEmployee && (
            <Button loading={sign.isPending} onClick={() => sign.mutate('employee')}>
              <Check className="size-4" aria-hidden />
              Ознакомлен, прошёл
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

// ---------- Таблицы ----------

export function RecordsTable({
  items,
  onOpen,
  hideEmployee,
}: {
  items: SafetyRecordItem[];
  onOpen: (r: SafetyRecordItem) => void;
  hideEmployee?: boolean;
}) {
  return (
    <div className="panel overflow-x-auto">
      <table className="w-full min-w-[720px] text-left">
        <thead>
          <tr className="border-b border-line text-[13px] text-steel">
            <th scope="col" className="px-4 py-2.5 font-medium">
              Дата
            </th>
            {!hideEmployee && (
              <th scope="col" className="px-4 py-2.5 font-medium">
                Сотрудник
              </th>
            )}
            <th scope="col" className="px-4 py-2.5 font-medium">
              Что прошли
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Проводил
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Подтверждение
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Срок
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((r) => (
            <tr key={r.id} className="hover:bg-paper/70">
              <td className="px-4 py-2.5 whitespace-nowrap">
                <button type="button" className="font-medium hover:text-link" onClick={() => onOpen(r)}>
                  {formatDay(r.date)}
                </button>
                <span className="block text-[12.5px] text-steel">№{r.number}</span>
              </td>
              {!hideEmployee && (
                <td className="px-4 py-2.5">
                  {r.employee.fullName}
                  <span className="block text-[12.5px] text-steel">{r.employee.positionName ?? ' '}</span>
                </td>
              )}
              <td className="px-4 py-2.5">
                {r.kind.name}
                {r.result !== 'passed' && (
                  <span className="block text-[12.5px]">
                    <ResultText r={r} />
                  </span>
                )}
                {r.reason && <span className="block text-[12.5px] text-steel">{r.reason}</span>}
              </td>
              <td className="px-4 py-2.5">{conductorText(r)}</td>
              <td className="px-4 py-2.5 text-[13px]">
                <SignMark
                  at={r.employeeSignedAt}
                  needed={r.kind.requiresSignature && Boolean(r.employee.userId)}
                />
                {r.conductedBy && (
                  <span className="block text-[12px] text-steel">
                    проводил: {r.conductorSignedAt ? 'да' : 'ждёт'}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 whitespace-nowrap">{r.nextDueAt ? formatDay(r.nextDueAt) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
